import {
	Alert,
	AlertDescription,
	AlertTitle,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@monbits/ui";
import { LogOut, Shield, ShieldCheck } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useMount } from "react-use";
import { toast } from "sonner";
import { Logo } from "src/components/logo";
import { TwoFactorBackupCodes } from "src/components/two-factor/two-factor-backup-codes";
import { TwoFactorSetup } from "src/components/two-factor/two-factor-setup";
import { useTwoFactor } from "src/hooks/use-two-factor";
import type { TwoFactorSetupResponse } from "src/providers/auth-provider";
import authProvider from "src/providers/auth-provider";

type Step = "setup" | "backup-codes";

const Setup2FAPage: React.FC = () => {
	const navigate = useNavigate();
	const { setupTwoFactor, enableTwoFactor, isSubmitting } = useTwoFactor();

	const [step, setStep] = React.useState<Step>("setup");
	const [setupData, setSetupData] =
		React.useState<TwoFactorSetupResponse | null>(null);
	const [backupCodes, setBackupCodes] = React.useState<string[]>([]);

	useMount(async () => {
		await authProvider.getCookie();
	});

	React.useEffect(() => {
		const initSetup = async () => {
			const data = await setupTwoFactor();
			if (data) {
				setSetupData(data);
			} else {
				toast.error("Failed to initialize 2FA setup");
			}
		};

		initSetup();
	}, [setupTwoFactor]);

	const handleSetupVerify = async (code: string) => {
		if (!setupData) return false;

		// Validate code before calling API
		if (!code || code.trim().length !== 6) {
			toast.error("Please enter a valid 6-digit code");
			return false;
		}

		const success = await enableTwoFactor(
			code,
			setupData.secret,
			setupData.backupCodes,
		);

		if (success) {
			setBackupCodes(setupData.backupCodes);
			setStep("backup-codes");
		}

		return success;
	};

	const handleComplete = () => {
		navigate("/", {
			state: { reloadAdminInfo: true },
		});
		toast.success("Two-factor authentication enabled", {
			description: "Your account is now more secure",
		});
	};

	const handleLogout = async () => {
		try {
			await authProvider.logout();
			navigate("/login", {
				state: {
					isNeedLogout: true,
				},
			});
			toast.info("Logged out successfully");
		} catch (error) {
			console.error("Logout failed:", error);
			toast.error("Failed to logout");
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center p-4 bg-background">
			<div className="w-full max-w-2xl">
				<div className="flex justify-between items-center mb-8">
					<div className="flex-1" />
					<Logo variant="full" size="xl" />
					<div className="flex-1 flex justify-end">
						<Button
							variant="ghost"
							size="sm"
							onClick={handleLogout}
							className="gap-2"
						>
							<LogOut className="h-4 w-4" />
							Logout
						</Button>
					</div>
				</div>

				{step === "setup" && setupData && (
					<Card>
						<CardHeader>
							<div className="flex items-center gap-3 mb-2">
								<div className="p-2 rounded-lg bg-primary/10">
									<Shield className="h-6 w-6 text-primary" />
								</div>
								<div>
									<CardTitle>Set Up Two-Factor Authentication</CardTitle>
									<CardDescription className="mt-1">
										Protect your account with an extra layer of security
									</CardDescription>
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-6">
							<Alert>
								<ShieldCheck className="h-4 w-4" />
								<AlertTitle>Security Requirement</AlertTitle>
								<AlertDescription>
									Two-factor authentication is required for all accounts. Please
									set it up to continue using the dashboard.
								</AlertDescription>
							</Alert>

							<TwoFactorSetup
								otpURL={setupData.otpUrl}
								secret={setupData.secret}
								onVerify={handleSetupVerify}
								isSubmitting={isSubmitting}
							/>
						</CardContent>
					</Card>
				)}

				{step === "backup-codes" && backupCodes.length > 0 && (
					<Card>
						<CardHeader>
							<CardTitle>Save Your Backup Codes</CardTitle>
							<CardDescription>
								Store these codes in a safe place. You can use them to access
								your account if you lose your authenticator device.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<TwoFactorBackupCodes
								codes={backupCodes}
								onClose={handleComplete}
								showWarning={true}
							/>
						</CardContent>
					</Card>
				)}
			</div>
		</div>
	);
};

export default Setup2FAPage;
